import { type FC, useMemo, useRef, useCallback, useState, useEffect } from "react";

import { ChevronLeft, ChevronRight } from "lucide-react";

import MainLayout from "@/pages/layouts/MainLayout";
import { ESSENTIAL_CATEGORIES, ESSENTIAL_MODULES } from "@/shared/constants/estateEssentials";
import { usePerson } from "@/shared/hooks/useAppContext";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { hasMinTier } from "@/shared/utils/tierOrder";
import { getFirstName } from "@/shared/utils/names";
import { trackEvent, EventType } from "@/shared/utils/core";
import { FullDiscoveryUpgradeDialog } from "@/pages/postloss/components/FullDiscoveryUpgradeDialog";

import type { EssentialModule } from "@/shared/types/estateEssentials";

import { useModuleProgress } from "@/shared/hooks/useModuleProgress";

import { EssentialsActions } from "./components/EssentialsActions";
import { ModuleCard } from "./components/ModuleCard";

function ModuleCarousel({
  modules,
  label,
  firstName,
  completedTaskIds,
  onClickLocked,
}: {
  modules: EssentialModule[];
  label: string;
  firstName?: string;
  completedTaskIds: Set<string>;
  onClickLocked?: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    setIsScrollable(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    updateScrollState();

    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    window.addEventListener("resize", updateScrollState);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  const scroll = useCallback((direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector("a")?.offsetWidth ?? 300;
    const gap = 16;
    const distance = cardWidth + gap;
    el.scrollBy({
      left: direction === "left" ? -distance : distance,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-lg font-medium">{label}</p>
        {isScrollable && (
          <div className="hidden md:flex items-center gap-1">
            <button
              type="button"
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className="flex items-center justify-center size-8 rounded-full border border-primary bg-white text-secondary hover:text-primary disabled:opacity-30 disabled:cursor-default transition-colors"
            >
              <ChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className="flex items-center justify-center size-8 rounded-full border border-primary bg-white text-secondary hover:text-primary disabled:opacity-30 disabled:cursor-default transition-colors"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        )}
      </div>
      <div className="relative -mx-4 md:mx-0">
        <div
          ref={scrollRef}
          onScroll={updateScrollState}
          onLoad={updateScrollState}
          className="flex gap-4 overflow-x-auto scrollbar-none px-4 md:px-0"
        >
          {modules.map((mod) => (
            <ModuleCard
              key={mod.id}
              module={mod}
              firstName={firstName}
              isComplete={!!mod.taskId && completedTaskIds.has(mod.taskId)}
              onClickLocked={onClickLocked}
            />
          ))}
        </div>
        {canScrollLeft && (
          <div
            className="pointer-events-none absolute left-0 top-0 bottom-0 z-10 hidden w-24 sm:block"
            style={{
              background: "linear-gradient(90deg, #F9F7F7 0%, rgba(249, 247, 247, 0.00) 100%)",
            }}
          />
        )}
        {canScrollRight && (
          <div
            className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 hidden w-24 sm:block"
            style={{
              background: "linear-gradient(270deg, #F9F7F7 0%, rgba(249, 247, 247, 0.00) 100%)",
            }}
          />
        )}
      </div>
    </div>
  );
}

export const EstateEssentialsPage: FC = () => {
  const person = usePerson();
  const { tier } = usePaymentTier(person?.estate_id);
  const isPremium = hasMinTier(tier, "basic");
  const [overlayOpen, setOverlayOpen] = useState(false);

  const modulesByCategory = useMemo(() => {
    const grouped = new Map<string, EssentialModule[]>();
    for (const mod of ESSENTIAL_MODULES) {
      const existing = grouped.get(mod.category) ?? [];
      existing.push(mod);
      grouped.set(mod.category, existing);
    }
    return grouped;
  }, []);

  useEffect(() => {
    trackEvent(EventType.ESSENTIALS_PAGE_VIEWED);
  }, []);

  const firstName = person?.first_name
    ? person.first_name
    : getFirstName(person?.full_name ?? undefined);

  const { taskStatusMap } = useModuleProgress();
  const completedTaskIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [taskId, status] of taskStatusMap) {
      if (status === "completed") ids.add(taskId);
    }
    return ids;
  }, [taskStatusMap]);

  return (
    <MainLayout header="Important to-dos" actions={<EssentialsActions />}>
      {!isPremium && (
        <FullDiscoveryUpgradeDialog open={overlayOpen} onOpenChange={setOverlayOpen} />
      )}

      <div className="space-y-8">
        {ESSENTIAL_CATEGORIES.map((cat) => {
          const modules = modulesByCategory.get(cat.id) ?? [];
          if (modules.length === 0) return null;
          return (
            <ModuleCarousel
              key={cat.id}
              modules={modules}
              label={cat.label}
              firstName={firstName}
              completedTaskIds={completedTaskIds}
              onClickLocked={!isPremium ? () => setOverlayOpen(true) : undefined}
            />
          );
        })}
      </div>
    </MainLayout>
  );
};
